import { makeStyles } from '@material-ui/styles'
import { listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import CodeBlock from 'core/components/CodeBlock'
import CopyToClipboard from 'core/components/CopyToClipboard'
import DocumentMeta from 'core/components/DocumentMeta'
import SimpleLink from 'core/components/SimpleLink'
import Info from 'core/components/validatedForm/Info'
import NumberedStepLabel from 'core/components/validatedForm/NumberedStepLabel'
import Button from 'core/elements/button'
import Divider from 'core/elements/Divider'
import ModalForm from 'core/elements/modal/ModalForm'
import ModalInfo from 'core/elements/modal/ModalInfo'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isPrivateRegistryEnabled } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { prop } from 'ramda'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

export default function AddServersPage({ addRoute }) {
  const { history } = useReactRouter()
  const classes = useStyles()
  const selectSessionState = prop<string, SessionState>(sessionStoreKey)
  const session = useSelector(selectSessionState)
  const { activeRegion } = session
  const { getUserPrefs } = useScopedPreferences()
  const { currentTenant, currentTenantName, currentRegion } = getUserPrefs(session?.username)
  const fqdn = window.location.origin

  const { loading: loadingTenants } = useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)

  const tenant = useMemo(() => tenants.find((tenant) => tenant.id === currentTenant), [tenants])

  const onClose = useCallback(() => {
    history.push(routeManager.routes.openstack.hosts.path())
  }, [history])

  const isPrivateRegistryEnabledEnv = isPrivateRegistryEnabled()

  // Get the onprem pcdctl version from session features
  const { onprem_pcdctl_version = 'latest' } = session?.features || {}

  return (
    <ModalForm
      route={addRoute}
      title={`Add a New Host`}
      onClose={onClose}
      panel="drawer"
      maxWidth={780}
      slideFrom="right"
      onBackdropClick={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
      footerButtonAlignment="end"
    >
      <DocumentMeta title="Add a New Host" />
      <div className={classes.root}>
        <ModalInfo
          icon="server"
          text={
            <>
              The host agent package is personalized for your account. Do not share it with anyone
              outside your organization. Once the host agent is installed, you will see the host in
              your list of{' '}
              <SimpleLink src={routeManager.routes.openstack.hosts.path()} onClick={onClose}>
                <b>Cluster Hosts</b>
              </SimpleLink>{' '}
              waiting to be assigned a role.
            </>
          }
        />
        <div className={classes.section}>
          <Text variant="subtitle2">Adding New Hosts</Text>
          <div className={classes.tutorial}>
            <NumberedStepLabel
              step={1}
              title="Login to your host terminal"
              className={classes.tutorialStep}
            />
            <NumberedStepLabel
              step={2}
              title={
                isPrivateRegistryEnabledEnv
                  ? 'Run the below command to download pcdctl binary on a jumphost.'
                  : 'Install the host agent by invoking below commands as root user:'
              }
              className={classes.tutorialStep}
            />
          </div>
          {isPrivateRegistryEnabledEnv ? (
            <>
              <div className={classes.codeBlocks}>
                <CopyToClipboard
                  copyText={`curl --user-agent "<YOUR_USER_AGENT_KEY>" https://pf9-airctl.s3-accelerate.amazonaws.com/${onprem_pcdctl_version}/pcdctl > pcdctl`}
                >
                  <CodeBlock>
                    <Text variant="body2">
                      {`curl --user-agent "<YOUR_USER_AGENT_KEY>" https://pf9-airctl.s3-accelerate.amazonaws.com/${onprem_pcdctl_version}/pcdctl > pcdctl`}
                    </Text>
                  </CodeBlock>
                </CopyToClipboard>
              </div>
              <div className={classes.tutorial}>
                <NumberedStepLabel
                  step={3}
                  title={
                    'Copy the pcdctl binary from the jumphost to `/usr/bin/pcdctl` on each host that is to be onboarded'
                  }
                  className={classes.tutorialStep}
                />
                <NumberedStepLabel
                  step={4}
                  title={'Install the host agent by invoking below commands as root user:'}
                  className={classes.tutorialStep}
                />
              </div>
              <div className={classes.codeBlocks}>
                <Text variant="body2"></Text>
                <CopyToClipboard
                  copyText={`pcdctl config set -u ${fqdn} -e ${
                    session?.username
                  } -r ${currentRegion ||
                    activeRegion} -t ${currentTenantName} --private-package-repo <PRIVATE_APT_REPO_URL>`}
                >
                  <CodeBlock>
                    <Text variant="body2">{`pcdctl config set -u ${fqdn} -e ${
                      session?.username
                    } -r ${currentRegion ||
                      activeRegion} -t ${currentTenantName} --private-package-repo <PRIVATE_APT_REPO_URL>`}</Text>
                  </CodeBlock>
                </CopyToClipboard>
                <CopyToClipboard copyText={'pcdctl prep-node --skip-repo-config'}>
                  <CodeBlock>
                    <Text variant="body2">pcdctl prep-node --skip-repo-config</Text>
                  </CodeBlock>
                </CopyToClipboard>
                <Text variant="body2" className={classes.pcdctlNote}>
                  <b>INFO</b>
                  <ul>
                    <li>
                      YOUR_USER_AGENT_KEY refers to the user agent key that has been provided by
                      Platform9.
                    </li>
                    <li>
                      PRIVATE_APT_REPO_URL refers to the URL of an internal APT repository used in
                      air-gapped or restricted environments. When set, packages are installed from
                      this repository instead of public sources.
                    </li>
                  </ul>
                </Text>
              </div>
            </>
          ) : (
            <div className={classes.codeBlocks}>
              <CopyToClipboard
                copyText={`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
              >
                <CodeBlock>
                  <Text variant="body2">
                    {`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
                  </Text>
                </CodeBlock>
              </CopyToClipboard>
              <CopyToClipboard
                copyText={`pcdctl config set -u ${fqdn} -e ${
                  session?.username
                } -r ${currentRegion || activeRegion} -t ${currentTenantName}`}
              >
                <CodeBlock>
                  <Text variant="body2">{`pcdctl config set -u ${fqdn} -e ${
                    session?.username
                  } -r ${currentRegion || activeRegion} -t ${currentTenantName}`}</Text>
                </CodeBlock>
              </CopyToClipboard>
              <CopyToClipboard copyText="pcdctl prep-node">
                <CodeBlock>
                  <Text variant="body2">{`pcdctl prep-node`}</Text>
                </CodeBlock>
              </CopyToClipboard>
              <Text variant="body2" className={classes.pcdctlNote}>
                <b>NOTE: </b> pcdctl installation might take some time to complete the setup and
                fully install its dependencies.
              </Text>
            </div>
          )}
        </div>
        <div className={classes.section}>
          <Text variant="subtitle2">Here is your info for quick use:</Text>
          <div className={classes.credentials}>
            <Text variant="body2">Account URL: </Text>
            <CopyToClipboard copyText={window.location.origin}>
              <CodeBlock>
                <Text variant="body2">{window.location.origin}</Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text variant="body2">Username: </Text>
            <CopyToClipboard copyText={session?.username}>
              <CodeBlock>
                <Text variant="body2">{session?.username}</Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text variant="body2">Region: </Text>
            <CopyToClipboard copyText={activeRegion}>
              <CodeBlock>
                <Text variant="body2">{activeRegion}</Text>
              </CodeBlock>
            </CopyToClipboard>
            <Text variant="body2">Tenant: </Text>
            <CopyToClipboard copyText={tenant?.name}>
              <CodeBlock>
                <Text variant="body2">{tenant?.name}</Text>
              </CodeBlock>
            </CopyToClipboard>
          </div>
          <Text variant="body2">
            Once the host agent is installed, you will see the host in your list of{' '}
            <SimpleLink src={routeManager.routes.openstack.hosts.path()}>Cluster Hosts</SimpleLink>{' '}
            waiting to be assigned a role.
          </Text>
          <Text variant="body2">
            <b>NOTE:</b> The host agent package is personalized for your account. Do not share it
            with anyone outside your organization.
          </Text>
          <Divider className={classes.divider} />
        </div>
        <div className={classes.section}>
          <Info title="Supported Distributions and Prerequisites" expanded={false}>
            <div className={classes.prerequisites}>
              <Text component="div" variant="body2">
                We currently support the following:{'   '}
                <b>Ubuntu 22.04 LTS - 64-bit (x86_64) and Ubuntu 24.04 LTS - 64-bit (x86_64)</b>.
              </Text>
              <Text component="div" variant="body2">
                If your host operating system isn't supported, please reach out to support.
              </Text>
            </div>
            {isPrivateRegistryEnabledEnv && (
              <div className={classes.prerequisites}>
                <Text component="div" variant="subtitle2">
                  APT repo pre-requisites (recommended):
                </Text>
                <Text component="div" variant="body2">
                  Ensure that the private APT repository is configured correctly on the node with
                  the required package list. Run apt update to confirm the repo is accessible and
                  packages can be fetched.
                </Text>
              </div>
            )}
          </Info>
        </div>
        <div className={classes.section}>
          <Info title="Troubleshooting" expanded={false}>
            <div className={classes.troubleshooting}>
              <Text variant="body2">Here are some quick tips if you don't see your host:</Text>
              <div className={classes.troubleshootingStep}>
                <NumberedStepLabel
                  step={1}
                  title="Verify that the host has outbound network connectivity to the internet and to the OpenStack controller"
                />
                <div className={classes.codeBlocks}>
                  <CopyToClipboard copyText={`ping www.google.com`}>
                    <CodeBlock>
                      <Text variant="body2">{`ping www.google.com`}</Text>
                    </CodeBlock>
                  </CopyToClipboard>
                  <CopyToClipboard copyText={`telnet www.google.com 443`}>
                    <CodeBlock>
                      <Text variant="body2">{`telnet www.google.com 443`}</Text>
                    </CodeBlock>
                  </CopyToClipboard>
                </div>
              </div>
              <div className={classes.troubleshootingStep}>
                <NumberedStepLabel
                  step={2}
                  title=" If you have a proxy server, make sure to configure the host agent to route traffic
                via the proxy:"
                />
                <div className={classes.codeBlocks}>
                  <CopyToClipboard
                    copyText={`pcdctl config set --proxy-url '<scheme>://[<username>[:<password>]@]<proxy-host>:<proxy-port>'`}
                  >
                    <CodeBlock>
                      <Text variant="body2">{`pcdctl config set --proxy-url '<scheme>://[<username>[:<password>]@]<proxy-host>:<proxy-port>'`}</Text>
                    </CodeBlock>
                  </CopyToClipboard>
                </div>
              </div>
              <div className={classes.troubleshootingStep}>
                <NumberedStepLabel
                  step={3}
                  title="Confirm that the host agent is running on your host"
                />
                <div className={classes.codeBlocks}>
                  <CopyToClipboard copyText={`sudo service pf9-hostagent status`}>
                    <CodeBlock>
                      <Text variant="body2">{`sudo service pf9-hostagent status`}</Text>
                    </CodeBlock>
                  </CopyToClipboard>
                </div>
              </div>
              <div className={classes.troubleshootingStep}>
                <NumberedStepLabel
                  step={4}
                  title="Check the host agent log and the comms log for errors"
                />
                <div className={classes.codeBlocks}>
                  <CopyToClipboard
                    copyText={`sudo cat /var/log/pf9/hostagent.log /var/log/pf9/comms/comms.log`}
                  >
                    <CodeBlock>
                      <Text variant="body2">{`sudo cat /var/log/pf9/hostagent.log /var/log/pf9/comms/comms.log`}</Text>
                    </CodeBlock>
                  </CopyToClipboard>
                </div>
              </div>
              <Text variant="body2">
                If these tips do not resolve the issue, please contact us for support. We're here to
                help!
              </Text>
            </div>
          </Info>
        </div>
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  tutorial: {
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  tutorialStep: {
    '& .title': {
      ...theme.typography.body2,
    },
  },
  prerequisites: {
    display: 'grid',
    padding: theme.spacing(1, 0),
    gridGap: theme.spacing(1),
  },
  troubleshooting: {
    display: 'grid',
    padding: theme.spacing(1, 0),
    gridGap: theme.spacing(3),
  },
  troubleshootingStep: {
    display: 'grid',
    gridGap: theme.spacing(1),
    '& .title': {
      ...theme.typography.body2,
      maxWidth: 612,
      textOverflow: 'wrap',
    },
  },
  codeBlocks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(5),
  },
  root: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  section: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  pcdctlNote: {
    marginTop: theme.spacing(1),

    '& ul': {
      marginBlockStart: theme.spacing(0.5),
      paddingInlineStart: theme.spacing(3),
    },
  },
  credentials: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 5fr',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0, 1),
  },
}))
